"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const COMMON_TARGETS = [90, 80, 70, 60];

function computeRequired(
  current: number,
  desired: number,
  weightPercent: number
): number {
  const weight = weightPercent / 100;
  return (desired - current * (1 - weight)) / weight;
}

function interpretRequired(
  required: number,
  strings: typeof FINAL_GRADE_CALCULATOR_STRINGS,
  fmt: NumberFormatter
): string {
  if (required <= 0) {
    return strings.alreadySecured;
  }
  if (required <= 100) {
    return t(strings.needAtLeast, {
      score: fmt(required, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    });
  }
  return strings.notPossible;
}

function parseInputs(
  current: string,
  desired: string,
  weight: string
): { current: number; desired: number; weight: number } | null {
  const c = parseNumberInput(current);
  const d = parseNumberInput(desired);
  const w = parseNumberInput(weight);
  if (c === null || d === null || w === null) {
    return null;
  }
  if (w <= 0 || w > 100) {
    return null;
  }
  return { current: c, desired: d, weight: w };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function formatRequiredForTable(
  required: number,
  strings: typeof FINAL_GRADE_CALCULATOR_STRINGS,
  fmt: NumberFormatter
): string {
  if (required <= 0) {
    return strings.alreadySecuredShort;
  }
  if (required > 100) {
    return strings.notPossibleShort;
  }
  return `${fmt(required, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/** English strings. A translated content file overrides these through `ui`. */
export const FINAL_GRADE_CALCULATOR_STRINGS = {
  currentGradePercent: "Current grade %",
  desiredGradePercent: "Desired grade %",
  finalExamWeightPercent: "Final exam weight %",
  requiredFinalExamScore: "Required final exam score",
  finalExamWeight: "Final exam weight",
  alreadySecured:
    "Your target grade is already secured — even a 0 on the final would keep you at or above it.",
  needAtLeast:
    "You need to score at least {score}% on the final exam to reach your target grade.",
  notPossible:
    "This target is not possible with the final exam alone — it would require a score above 100%. Consider a lower target or ask about extra credit.",
  enterInputsPrompt:
    "Enter your current grade, desired grade, and a final exam weight between 1% and 100% to see the required score.",
  requiredScoresForCommonTargets: "Required Scores for Common Targets",
  basedOnCurrentGrade:
    "Based on your current grade of {current}% and a final worth {weight}% of the course.",
  targetGrade: "Target grade",
  requiredFinalScore: "Required final score",
  alreadySecuredShort: "Already secured",
  notPossibleShort: "Not possible",
} as const;

export default function FinalGradeCalculatorWidget() {
  const [current, setCurrent] = useState("85");
  const [desired, setDesired] = useState("90");
  const [weight, setWeight] = useState("25");

  const strings = useToolUi(FINAL_GRADE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();

  const inputs = parseInputs(current, desired, weight);
  const required = inputs
    ? computeRequired(inputs.current, inputs.desired, inputs.weight)
    : null;

  return (
    <>
      {/* Inputs */}
      <div className="flex flex-wrap items-end gap-6 rounded-lg border border-border bg-muted/30 p-5">
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="current-grade"
          >
            {strings.currentGradePercent}
          </label>
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="current-grade"
            max={150}
            min={0}
            onChange={(e) => setCurrent(e.target.value)}
            step="0.1"
            type="number"
            value={current}
          />
        </div>
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="desired-grade"
          >
            {strings.desiredGradePercent}
          </label>
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="desired-grade"
            max={150}
            min={0}
            onChange={(e) => setDesired(e.target.value)}
            step="0.1"
            type="number"
            value={desired}
          />
        </div>
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="final-weight"
          >
            {strings.finalExamWeightPercent}
          </label>
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="final-weight"
            max={100}
            min={1}
            onChange={(e) => setWeight(e.target.value)}
            step="0.1"
            type="number"
            value={weight}
          />
        </div>
      </div>

      {/* Result */}
      {inputs && required !== null && (
        <section className="space-y-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <StatCard
              label={strings.requiredFinalExamScore}
              value={`${fmt(required, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
            />
            <StatCard
              label={strings.finalExamWeight}
              value={`${inputs.weight}%`}
            />
          </div>
          <p className="rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {interpretRequired(required, strings, fmt)}
          </p>
        </section>
      )}
      {!inputs && (
        <p className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm leading-relaxed">
          {strings.enterInputsPrompt}
        </p>
      )}

      {/* Common targets table */}
      {inputs && (
        <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.requiredScoresForCommonTargets}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {t(strings.basedOnCurrentGrade, {
              current: inputs.current,
              weight: inputs.weight,
            })}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border border-b">
                  <th className="py-2 pr-4 font-medium text-foreground">
                    {strings.targetGrade}
                  </th>
                  <th className="py-2 font-medium text-foreground">
                    {strings.requiredFinalScore}
                  </th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {COMMON_TARGETS.map((target) => (
                  <tr className="border-border/50 border-b" key={target}>
                    <td className="py-2 pr-4 font-medium text-foreground">
                      {target}%
                    </td>
                    <td className="py-2">
                      {formatRequiredForTable(
                        computeRequired(inputs.current, target, inputs.weight),
                        strings,
                        fmt
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
